import { ArrowRight, AlertTriangle, Check } from 'lucide-react'
import { useAssessment } from '../../context/AssessmentContext'
import Shell from '../ui/Shell'
import { pulse } from '../ui/LightField'
import { AREA_BY_SLUG, HEADERS, OVERRIDE_WARNING } from '../../data/onboarding'
import { sortByPriority } from '../../lib/priority'
import { getProgress, STEP_LABEL } from '../../lib/flow'

/**
 * "Which is your main focus?" — shown only when more than one area is selected.
 * The recommendation comes from the production priority formula; choosing a
 * lower-priority area reveals the same warning the real app shows, and the
 * screen keeps its Continue button so the warning can actually be read.
 */
function PrioritizationStep() {
  const { state, dispatch } = useAssessment()
  const ordered = sortByPriority(state.selectedAddictions, (s) => state.gates[s])
  const recommended = ordered[0]
  const chosen = state.primaryAddiction
  const overriding = chosen && chosen !== recommended

  const pick = (slug, el) => {
    if (el) {
      const r = el.getBoundingClientRect()
      pulse(0.6, { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight })
    }
    dispatch({ type: 'SET_PRIMARY', payload: { slug, overridden: slug !== recommended } })
  }

  const next = () => {
    if (!chosen) return
    dispatch({ type: 'SET_SCREEN', payload: state.gates[chosen]?.status === 'recovery' ? 'barc10' : 'screener' })
  }

  const back = () => {
    dispatch({ type: 'SET_AREA_INDEX', payload: state.selectedAddictions.length - 1 })
    dispatch({ type: 'SET_SCREEN', payload: 'recoveryGate' })
  }

  return (
    <Shell label={STEP_LABEL.prioritization} progress={getProgress(state)} onBack={back}>
      <main className="flow">
        <div className="flow__head">
          <h1>{HEADERS.prioritization}</h1>
          <p className="flow__lede">We start your plan here. You can change it.</p>
        </div>

        <div className="rows" role="radiogroup" aria-label="Main focus">
          {ordered.map((slug) => {
            const area = AREA_BY_SLUG[slug]
            const gate = state.gates[slug]
            return (
              <button
                key={slug}
                type="button"
                role="radio"
                aria-checked={chosen === slug}
                className="row row--no-key"
                onClick={(e) => pick(slug, e.currentTarget)}
              >
                <span className="row__text">
                  <span className="row__title">
                    {area?.label}
                    {slug === recommended && <span className="pill pill--rec">Recommended</span>}
                  </span>
                  <span className="row__sub">
                    {gate?.status === 'recovery' ? 'In recovery' : 'Currently using'}
                  </span>
                </span>
                <span className="row__check" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>
              </button>
            )
          })}
        </div>

        {overriding && (
          <div className="notice" role="status">
            <AlertTriangle size={18} strokeWidth={2} />
            <div>
              <p className="notice__title">{OVERRIDE_WARNING.title}</p>
              <p className="notice__body">{OVERRIDE_WARNING.body(AREA_BY_SLUG[recommended]?.label ?? 'the top option')}</p>
            </div>
          </div>
        )}

        <div className="flow__actions">
          <button type="button" className="btn btn--block" onClick={next} disabled={!chosen}>
            Continue
            <ArrowRight size={18} strokeWidth={2.25} />
          </button>
        </div>
      </main>
    </Shell>
  )
}

export default PrioritizationStep
